/**
 * Offerings — /console/stalls/:stallId/offerings (job 171a06d5).
 *
 * The Koroi Labs walk reached a live, public stall with zero offerings and
 * no console surface to add one. An offering is a priced `action` content
 * item: POST /stoa/stalls/:id/content takes the owner's operator Bearer
 * (loadStallForWrite), and the manifest then lists it with a price,
 * offering_hash and hire_url. No agent key is involved, which is what a
 * Path B operator (key held on their own machine) needs.
 *
 * The list below the form is the manifest's, i.e. what a buyer reads, not
 * what this page believes it wrote.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { ApiError, type ConsoleStall, type StallOffering } from '../api/client';

/** Same rule the server applies (contentUpsertSchema.slug). */
export const OFFERING_SLUG_RE = /^[a-z0-9][a-z0-9._-]{0,118}$/;

export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

const OfferingsPage: React.FC = () => {
  const { stallId = '' } = useParams<{ stallId: string }>();
  const { api } = useOperatorAuth();

  const [stall, setStall] = useState<ConsoleStall | null>(null);
  const [offerings, setOfferings] = useState<StallOffering[] | null>(null);
  const [listNote, setListNote] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [summary, setSummary] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState<string | null>(null);

  const loadOfferings = useCallback(async () => {
    setListNote(null);
    try {
      const m = await api.getStallManifest(stallId);
      setOfferings(m.offerings ?? []);
    } catch (err) {
      setOfferings(null);
      setListNote(
        err instanceof ApiError && err.status === 404
          ? 'Buyers cannot see this stall yet, so there is no published list. Offerings you add now appear once it is live.'
          : `Could not read the published offerings: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  }, [api, stallId]);

  useEffect(() => {
    let cancelled = false;
    api
      .getConsoleStalls()
      .then((res) => {
        if (!cancelled) setStall(res.stalls.find((s) => s.stall_id === stallId) ?? null);
      })
      .catch(() => {
        /* the header falls back to the id */
      });
    loadOfferings();
    return () => {
      cancelled = true;
    };
  }, [api, stallId, loadOfferings]);

  const effectiveSlug = slugTouched ? slug : slugFromTitle(title);
  const priceNum = Number(price);
  const priceValid = price.trim() !== '' && Number.isFinite(priceNum) && priceNum >= 0 && priceNum <= 1_000_000;
  const slugValid = OFFERING_SLUG_RE.test(effectiveSlug);
  const titleValid = title.trim().length > 0 && title.trim().length <= 300;
  const replacing = offerings?.some((o) => o.slug === effectiveSlug) ?? false;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titleValid || !slugValid || !priceValid || busy) return;
    setBusy(true);
    setError(null);
    setPublished(null);
    try {
      const item = await api.publishOffering(stallId, {
        slug: effectiveSlug,
        title: title.trim(),
        summary: summary.trim() || undefined,
        price_aex: priceNum,
      });
      setPublished(item.slug);
      setTitle('');
      setSlug('');
      setSlugTouched(false);
      setSummary('');
      setPrice('');
      await loadOfferings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publishing the offering failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="console-page offerings-page">
      <div className="onboarding-muted">
        <Link to="/console">&larr; Back to console</Link>
      </div>
      <h2 className="agent-section-header">
        Offerings · {stall?.name ?? <span className="mono">{stallId}</span>}
      </h2>
      <p className="onboarding-muted">
        An offering is something a buyer can hire this stall for at the price you set here. It is
        signed into the stall&apos;s manifest and listed on the board with a hire link.
      </p>

      <form className="onboarding-fields" onSubmit={submit} aria-label="New offering">
        <label className="onboarding-field">
          <span>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
            placeholder="Plain-English summary of one technical doc"
            disabled={busy}
          />
        </label>
        <label className="onboarding-field">
          <span>Slug</span>
          <input
            type="text"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            disabled={busy}
          />
        </label>
        {effectiveSlug !== '' && !slugValid && (
          <div className="onboarding-muted" role="note">
            Lowercase letters, digits and . _ - only, starting with a letter or digit.
          </div>
        )}
        {replacing && (
          <div className="onboarding-muted" role="note">
            An offering with this slug is already published. Publishing replaces it, price included.
          </div>
        )}
        <label className="onboarding-field">
          <span>Summary (optional)</span>
          <input
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={2000}
            disabled={busy}
          />
        </label>
        <label className="onboarding-field">
          <span>Price (AEX)</span>
          <input
            type="number"
            min="0"
            max="1000000"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="5"
            disabled={busy}
          />
        </label>
        <div className="onboarding-actions">
          <button
            type="submit"
            className="action-btn primary"
            disabled={busy || !titleValid || !slugValid || !priceValid}
          >
            {busy ? 'Publishing…' : 'Publish offering'}
          </button>
        </div>
      </form>

      {error && <div className="auth-error" role="alert">{error}</div>}
      {published && (
        <div className="onboarding-muted" role="status">
          Published <span className="mono">{published}</span>.
        </div>
      )}

      <h3 className="agent-section-header">Published offerings</h3>
      {listNote && <div className="onboarding-muted">{listNote}</div>}
      {offerings && offerings.length === 0 && (
        <div className="empty-state-inline">No offerings yet.</div>
      )}
      {offerings && offerings.length > 0 && (
        <table className="wallet-table" aria-label="Published offerings">
          <thead>
            <tr>
              <th>Offering</th>
              <th>Kind</th>
              <th>Price (AEX)</th>
              <th>Hire link</th>
            </tr>
          </thead>
          <tbody>
            {offerings.map((o) => (
              <tr key={o.slug}>
                <td>
                  {o.label} <span className="mono onboarding-muted">{o.slug}</span>
                </td>
                <td>{o.kind}</td>
                <td>{o.price_aex ?? 'unpriced'}</td>
                <td className="mono">{o.hire_url ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OfferingsPage;
